import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const LoginPage: React.FC = () => {
  const { colors } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      // Call backend login API
      const response = await authApi.login(email, password);
      const { token, user } = response.data.data;
      localStorage.setItem('token', token);
      // Call context login to update state
      await login(user, token);
      // Redirect to dashboard or admin panel
      if (user.role && user.role.toLowerCase() === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background.default }}>
      <div className="max-w-md w-full space-y-8 p-8 rounded-lg shadow-lg" 
           style={{ 
             backgroundColor: colors.background.paper,
             boxShadow: `0 10px 30px -10px ${colors.accent.mint}30`
           }}>
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold" 
              style={{ 
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
            Sign in to your account
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">Email address</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:z-10 sm:text-sm"
                style={{
                  backgroundColor: colors.background.default,
                  borderColor: colors.border,
                  color: colors.text.primary,
                  borderRadius: '0.375rem 0.375rem 0 0'
                }}
                placeholder="Email address"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = colors.accent.mint;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.accent.mint}30`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:z-10 sm:text-sm"
                style={{
                  backgroundColor: colors.background.default,
                  borderColor: colors.border,
                  color: colors.text.primary,
                  borderRadius: '0 0 0.375rem 0.375rem'
                }}
                placeholder="Password"
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = colors.accent.mint;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.accent.mint}30`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = colors.border;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>
          {error && <div className="text-center text-sm" style={{ color: colors.error.main }}>{error}</div>}
          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60 transition-all duration-300 hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                color: colors.primary.contrastText
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = `linear-gradient(135deg, ${colors.accent.mint} 0%, ${colors.primary.main} 100%)`;
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`;
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage; 